import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { performLogout } from "../../utils/logoutHandler";
import { useRouteAccess } from "../../contexts/RouteAccessContext";
import "./sidebar.css";
import "./SuperadminSidebar.css";

const SuperadminSidebar = () => {
    const { t } = useTranslation('sidebar');
    const { hasAccess } = useRouteAccess();
    const location = useLocation();
    const navigate = useNavigate();

    const [isEnquiriesOpen, setIsEnquiriesOpen] = useState(
        location.pathname === '/superadmin_enquiries' || location.pathname === '/superadmin_cyberinsurify_enquiries'
    );
    const [isInternalTeamOpen, setIsInternalTeamOpen] = useState(
        location.pathname === '/internal_departments' || location.pathname === '/create_internal_department' || location.pathname === '/internal_roles' || location.pathname === '/internal_staff'
    );


    useEffect(() => {
        if (location.pathname === '/superadmin_enquiries' || location.pathname === '/superadmin_cyberinsurify_enquiries') {
            setIsEnquiriesOpen(true);
        }
    }, [location.pathname]);

    const isActive = (path) => {
        return location.pathname === path ? 'active' : '';
    };

    const handleLogout = async () => {
        try {
            await performLogout('1', 'ADMIN'); // Superadmin uses admin paths
            toast.success("User logged out successfully");
        } catch (error) {
            console.error("Logout failed", error);
            toast.error(`Logout failed: ${error.message}`);
            localStorage.clear();
            navigate('/login');
        }
    };

    return (
        <aside id="mainSidebar" className="main-sidebar sa-sidebar-wrapper">
            <Link to="/superadmin_dashboard" className="sa-brand">
                <div className="sa-brand-icon-wrapper">
                    <i className="fas fa-crown"></i>
                </div>
                <span className="sa-brand-text">CARA Superadmin</span>
            </Link>

            <div className="sa-sidebar-content">
                <nav className="sa-nav">
                    <ul className="sa-nav-list">
                        {hasAccess('/superadmin_dashboard') && (
<li className="sa-nav-item">
                            <Link to="/superadmin_dashboard" className={`sa-nav-link ${isActive('/superadmin_dashboard')}`}>
                                <div className="sa-nav-link-left">
                                    <i className="sa-icon fas fa-tachometer-alt"></i>
                                    <span>Dashboard</span>
                                </div>
                            </Link>
                        </li>
)}
                        {hasAccess('/superadmin_storage') && (
<li className="sa-nav-item">
                            <Link to="/superadmin_storage" className={`sa-nav-link ${isActive('/superadmin_storage')}`}>
                                <div className="sa-nav-link-left">
                                    <i className="sa-icon fas fa-database"></i>
                                    <span>Storage Management</span>
                                </div>
                            </Link>
                        </li>
)}
                        {hasAccess('/superadmin_ai_usage') && (
<li className="sa-nav-item">
                            <Link to="/superadmin_ai_usage" className={`sa-nav-link ${isActive('/superadmin_ai_usage')}`}>
                                <div className="sa-nav-link-left">
                                    <i className="sa-icon fas fa-robot"></i>
                                    <span>{t('menu.superadmin.aiUsage')}</span>
                                </div>
                            </Link>
                        </li>
)}
                        {hasAccess('/superadmin_announcements') && (
<li className="sa-nav-item">
                            <Link to="/superadmin_announcements" className={`sa-nav-link ${isActive('/superadmin_announcements')}`}>
                                <div className="sa-nav-link-left">
                                    <i className="sa-icon fas fa-bullhorn"></i>
                                    <span>Announcements</span>
                                </div>
                            </Link>
                        </li>
)}
                        {hasAccess('/superadmin_upgrade_requests') && (
<li className="sa-nav-item">
                            <Link to="/superadmin_upgrade_requests" className={`sa-nav-link ${isActive('/superadmin_upgrade_requests')}`}>
                                <div className="sa-nav-link-left">
                                    <i className="sa-icon fas fa-arrow-circle-up"></i>
                                    <span>Upgrade Requests</span>
                                </div>
                            </Link>
                        </li>
)}
                        
                        {(hasAccess('/internal_departments') || hasAccess('/internal_roles') || hasAccess('/internal_staff')) && (
<li className="sa-nav-item sa-has-submenu">
                            <div 
                                className={`sa-nav-link sa-submenu-toggle ${isActive('/superadmin_enquiries') || isActive('/superadmin_cyberinsurify_enquiries') ? 'active-parent' : ''}`} 
                                onClick={() => setIsEnquiriesOpen(!isEnquiriesOpen)}
                            >
                                <div className="sa-nav-link-left">
                                    <i className="sa-icon fas fa-envelope"></i>
                                    <span>Website Enquiries</span>
                                </div>
                                <i className={`sa-arrow fas fa-chevron-${isEnquiriesOpen ? 'down' : 'right'}`}></i>
                            </div>
                            <ul className="sa-submenu-list" style={{ display: isEnquiriesOpen ? 'block' : 'none' }}>
                                {hasAccess('/superadmin_enquiries') && (
<li className="sa-submenu-item">
                                    <Link to="/superadmin_enquiries" className={`sa-submenu-link ${isActive('/superadmin_enquiries')}`}>
                                        <i className="sa-submenu-icon fas fa-circle"></i>
                                        <span>CARAgrc</span>
                                    </Link>
                                </li>
)}
                                {hasAccess('/superadmin_cyberinsurify_enquiries') && (
<li className="sa-submenu-item">
                                    <Link to="/superadmin_cyberinsurify_enquiries" className={`sa-submenu-link ${isActive('/superadmin_cyberinsurify_enquiries')}`}>
                                        <i className="sa-submenu-icon fas fa-circle"></i>
                                        <span>CyberInsurify</span>
                                    </Link>
                                </li>
)}
                            </ul>
                        </li>
)}

                        {hasAccess('/superadmin_packages') && (
<li className="sa-nav-item">
                            <Link to="/superadmin_packages" className={`sa-nav-link ${isActive('/superadmin_packages')}`}>
                                <div className="sa-nav-link-left">
                                    <i className="sa-icon fas fa-boxes"></i>
                                    <span>Manage Packages</span>
                                </div>
                            </Link>
                        </li>
)}

                        {hasAccess('/superadmin_create_package') && (
<li className="sa-nav-item">
                            <Link to="/superadmin_create_package" className={`sa-nav-link ${isActive('/superadmin_create_package')}`}>
                                <div className="sa-nav-link-left">
                                    <i className="sa-icon fas fa-box-open"></i>
                                    <span>Create Package</span>
                                </div>
                            </Link>
                        </li>
)}

                        {hasAccess('/create_cara_admin_v2') && (
<li className="sa-nav-item">
                            <Link to="/create_cara_admin_v2" className={`sa-nav-link ${isActive('/create_cara_admin_v2')}`}>
                                <div className="sa-nav-link-left">
                                    <i className="sa-icon fas fa-user-plus"></i>
                                    <span>Create CARA Admin (V2)</span>
                                </div>
                            </Link>
                        </li>
)}
                        {hasAccess('/superadmin_manage_admins') && (
<li className="sa-nav-item">
                            <Link to="/superadmin_manage_admins" className={`sa-nav-link ${isActive('/superadmin_manage_admins')}`}>
                                <div className="sa-nav-link-left">
                                    <i className="sa-icon fas fa-user-edit"></i>
                                    <span>Manage CARA Admins</span>
                                </div>
                            </Link>
                        </li>
)}
                        {(hasAccess('/client_360') || hasAccess('/superadmin_manage_admins')) && (
<li className="sa-nav-item">
                            <Link to="/client_360" className={`sa-nav-link ${location.pathname.startsWith('/client_360') ? 'active' : ''}`}>
                                <div className="sa-nav-link-left">
                                    <i className="sa-icon fas fa-chart-pie"></i>
                                    <span>Client 360°</span>
                                </div>
                            </Link>
                        </li>
)}
                        {(hasAccess('/superadmin_onboarding') || hasAccess('/superadmin_manage_admins')) && (
<li className="sa-nav-item">
                            <Link to="/superadmin_onboarding" className={`sa-nav-link ${location.pathname.startsWith('/superadmin_onboarding') ? 'active' : ''}`}>
                                <div className="sa-nav-link-left">
                                    <i className="sa-icon fas fa-clipboard-check"></i>
                                    <span>Client Onboarding</span>
                                </div>
                            </Link>
                        </li>
)}
                        {hasAccess('/superadmin_tenure_tracker') && (
<li className="sa-nav-item">
                              <Link to="/superadmin_tenure_tracker" className={`sa-nav-link ${isActive('/superadmin_tenure_tracker')}`}>
                                  <div className="sa-nav-link-left">
                                      <i className="sa-icon fas fa-calendar-check"></i>
                                      <span>Tenure Tracker</span>
                                  </div>
                              </Link>
                          </li>
)}
                        {hasAccess('/create_cara_vrm') && (
<li className="sa-nav-item">
                            <Link to="/create_cara_vrm" className={`sa-nav-link ${isActive('/create_cara_vrm')}`}>
                                <div className="sa-nav-link-left">
                                    <i className="sa-icon fas fa-user-tie"></i>
                                    <span>Create VRM</span>
                                </div>
                            </Link>
                        </li>
)}
                        {hasAccess('/superadmin_vrm_mapping') && (
<li className="sa-nav-item">
                            <Link to="/superadmin_vrm_mapping" className={`sa-nav-link ${isActive('/superadmin_vrm_mapping')}`}>
                                <div className="sa-nav-link-left">
                                    <i className="sa-icon fas fa-sitemap"></i>
                                    <span>VRM Mapping</span>
                                </div>
                            </Link>
                        </li>
)}
                        {hasAccess('/superadmin_vrm_mapping') && (
<li className="sa-nav-item">
                            <Link to="/superadmin_vrm_utilization" className={`sa-nav-link ${isActive('/superadmin_vrm_utilization')}`}>
                                <div className="sa-nav-link-left">
                                    <i className="sa-icon fas fa-clock"></i>
                                    <span>VRM Utilization</span>
                                </div>
                            </Link>
                        </li>
)}
                        {hasAccess('/create_offsec_staff') && (
<li className="sa-nav-item">
                            <Link to="/create_offsec_staff" className={`sa-nav-link ${isActive('/create_offsec_staff')}`}>
                                <div className="sa-nav-link-left">
                                    <i className="sa-icon fas fa-user-secret"></i>
                                    <span>Create OffSec Team</span>
                                </div>
                            </Link>
                        </li>
)}
                        {hasAccess('/superadmin_offsec_staff_mapping') && (
<li className="sa-nav-item">
                            <Link to="/superadmin_offsec_staff_mapping" className={`sa-nav-link ${isActive('/superadmin_offsec_staff_mapping')}`}>
                                <div className="sa-nav-link-left">
                                    <i className="sa-icon fas fa-shield-alt"></i>
                                    <span>OffSec Team Mapping</span>
                                </div>
                            </Link>
                        </li>
)}
                        {hasAccess('/policy_library_admin') && (
<li className="sa-nav-item">
                            <Link to="/policy_library_admin" className={`sa-nav-link ${isActive('/policy_library_admin')}`}>
                                <div className="sa-nav-link-left">
                                    <i className="sa-icon fas fa-book"></i>
                                    <span>Policy Library</span>
                                </div>
                            </Link>
                        </li>
)}
                        {hasAccess('/create_super_admin') && (
<li className="sa-nav-item">
                            <Link to="/create_super_admin" className={`sa-nav-link ${isActive('/create_super_admin')}`}>
                                <div className="sa-nav-link-left">
                                    <i className="sa-icon fas fa-user-shield"></i>
                                    <span>Create SuperAdmin</span>
                                </div>
                            </Link>
                        </li>
)}
                                                {(hasAccess('/internal_departments') || hasAccess('/internal_roles') || hasAccess('/internal_staff')) && (
<li className="sa-nav-item sa-has-submenu">
                            <div 
                                className={`sa-nav-link sa-submenu-toggle ${isActive('/internal_departments') || isActive('/create_internal_department') ? 'active-parent' : ''}`} 
                                onClick={() => setIsInternalTeamOpen(!isInternalTeamOpen)}
                            >
                                <div className="sa-nav-link-left">
                                    <i className="sa-icon fas fa-users-cog"></i>
                                    <span>Internal Team</span>
                                </div>
                                <i className={`sa-arrow fas fa-chevron-${isInternalTeamOpen ? 'down' : 'right'}`}></i>
                            </div>
                            <ul className="sa-submenu-list" style={{ display: isInternalTeamOpen ? 'block' : 'none' }}>
                                {hasAccess('/internal_departments') && (
<li className="sa-submenu-item">
                                    <Link to="/internal_departments" className={`sa-submenu-link ${isActive('/internal_departments') || isActive('/create_internal_department')}`}>
                                        <i className="sa-submenu-icon fas fa-circle"></i>
                                        <span>Departments</span>
                                    </Link>
                                </li>
)}
                                {hasAccess('/internal_roles') && (
<li className="sa-submenu-item">
                                    <Link to="/internal_roles" className={`sa-submenu-link ${isActive('/internal_roles') || isActive('/create_internal_role')}`}>
                                        <i className="sa-submenu-icon fas fa-circle"></i>
                                        <span>Roles</span>
                                    </Link>
                                </li>
)}
                                {hasAccess('/internal_staff') && (
<li className="sa-submenu-item">
                                    <Link to="/internal_staff" className={`sa-submenu-link ${isActive('/internal_staff') || isActive('/create_internal_staff')}`}>
                                        <i className="sa-submenu-icon fas fa-circle"></i>
                                        <span>Staff</span>
                                    </Link>
                                </li>
)}
                            </ul>
                        </li>
)}

                    </ul>
                </nav>
            </div>

            <div className="sa-sidebar-footer">
                <div className="sa-nav-link sa-logout-link" onClick={() => {
                    Swal.fire({
                        title: 'Logout Session?',
                        text: "You will be securely logged out.",
                        icon: 'warning',
                        showCancelButton: true,
                        confirmButtonColor: '#ef4444',
                        cancelButtonColor: '#374151',
                        confirmButtonText: 'Yes, logout',
                        background: '#1f2937',
                        color: '#fff',
                        customClass: {
                            popup: 'sa-swal-popup',
                            title: 'sa-swal-title'
                        }
                    }).then((result) => {
                        if (result.isConfirmed) {
                            handleLogout();
                        }
                    });
                }}>
                    <div className="sa-nav-link-left">
                        <i className="sa-icon fas fa-power-off"></i>
                        <span>Secure Logout</span>
                    </div>
                </div>
            </div>
        </aside>
    );
};

export default SuperadminSidebar;
